import {format} from 'date-fns';
import {useState} from 'react';

import {useGetAskStatsV2Query} from '~/store/rtkQuery';

export const AskStatsV2 = () => {
  const [periodLength, setPeriodLength] = useState(14);
  const [startDate, setStartDate] = useState('2023-04-03');

  const {data, error, isLoading, refetch} = useGetAskStatsV2Query({periodLength, startDate});

  if (error) {
    return (
      <div className="p-4">
        <div role="alert" className="alert alert-error">
          <span>Error: {error.message}</span>
        </div>

        <button className="btn btn-primary" onClick={() => refetch()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="p-4">
      {isLoading && <span className="loading loading-dots loading-md"></span>}
      {!!data && (
        <div>
          <div className="mb-2">
            <small>
              <strong>As of:</strong>{' '}
              {format(data.meta.asOfDate, 'yyyy-MM-dd hh:mm')}
            </small>
          </div>
          <div className="overflow-x-scroll">
            <table className="table table-xs table-pin-rows table-pin-cols">
              <thead>
                <tr>
                  <th />
                  {data.periods.map(period => {
                    return <th>{period.periodStart.substring(0, 10)}</th>;
                  })}
                </tr>
              </thead>

              <tbody>
                <tr>
                  <th>Questions Asked</th>
                  {data.periods.map(period => {
                    return <td>{period.summary.numQuestionsAsked} </td>;
                  })}
                </tr>
                <tr>
                  <th>Questions Answered</th>
                  {data.periods.map(period => {
                    return <td>{period.summary.numQuestionsAnswered} </td>;
                  })}
                </tr>
                <tr>
                  <th>Questions Answered with linked content</th>
                  {data.periods.map(period => {
                    return (
                      <td>
                        {period.summary.numQuestionsAnsweredWithLinkedContent}{' '}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <th>Questions Rejected</th>
                  {data.periods.map(period => {
                    return <td>{period.summary.numQuestionsRejected} </td>;
                  })}
                </tr>

                <tr className="bg-info">
                  <th className="bg-info">Queue Time</th>
                  {data.periods.map(() => (
                    <td />
                  ))}
                </tr>
                <tr>
                  <th>Mean (mins)</th>
                  {data.periods.map(period => {
                    return (
                      <td>
                        {period.summary.averageQueueTimeMinutes.toFixed(0)}{' '}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <th>Mean (hours)</th>
                  {data.periods.map(period => {
                    return (
                      <td>
                        {(period.summary.averageQueueTimeMinutes / 60).toFixed(
                          2,
                        )}{' '}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <th>Mean (days)</th>
                  {data.periods.map(period => {
                    return (
                      <td>
                        {(
                          period.summary.averageQueueTimeMinutes /
                          (60 * 24)
                        ).toFixed(2)}{' '}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <th>Median (mins)</th>
                  {data.periods.map(period => {
                    return (
                      <td>
                        {period.summary.medianQueueTimeMinutes.toFixed(0)}{' '}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <th>Median (hrs)</th>
                  {data.periods.map(period => {
                    return (
                      <td>
                        {(period.summary.medianQueueTimeMinutes / 60).toFixed(
                          2,
                        )}{' '}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <th>Median (days)</th>
                  {data.periods.map(period => {
                    return (
                      <td>
                        {(
                          period.summary.medianQueueTimeMinutes /
                          (60 * 24)
                        ).toFixed(2)}{' '}
                      </td>
                    );
                  })}
                </tr>

                <tr className="bg-info">
                  <th className="bg-info">By Category</th>
                  {data.periods.map(() => (
                    <td />
                  ))}
                </tr>

                {data.meta.categories.map(category => {
                  return (
                    <tr key={category}>
                      <th>{category}</th>
                      {data.periods.map(period => {
                        return <td>{period.byCategory[category]}</td>;
                      })}
                    </tr>
                  );
                })}

                <tr className="bg-info">
                  <th className="bg-info">By Rejection Reason</th>
                  {data.periods.map(() => (
                    <td />
                  ))}
                </tr>

                {data.meta.rejectionReasons.map(rejectionReason => {
                  return (
                    <tr key={rejectionReason}>
                      <th>{rejectionReason}</th>
                      {data.periods.map(period => {
                        return (
                          <td>{period.byRejectionReason[rejectionReason]}</td>
                        );
                      })}
                    </tr>
                  );
                })}

                <tr className="bg-info">
                  <th className="bg-info">Users asking Xth question</th>
                  {data.periods.map(() => (
                    <td />
                  ))}
                </tr>

                <tr>
                  <th>1st</th>
                  {data.periods.map(period => (
                    <td>{period.summary.usersAskingXthQuestion['1st']}</td>
                  ))}
                </tr>

                <tr>
                  <th>2nd</th>
                  {data.periods.map(period => (
                    <td>{period.summary.usersAskingXthQuestion['2nd']}</td>
                  ))}
                </tr>

                <tr>
                  <th>3rd</th>
                  {data.periods.map(period => (
                    <td>{period.summary.usersAskingXthQuestion['3rd']}</td>
                  ))}
                </tr>

                <tr>
                  <th>4th</th>
                  {data.periods.map(period => (
                    <td>{period.summary.usersAskingXthQuestion['4th']}</td>
                  ))}
                </tr>

                <tr>
                  <th>5th+</th>
                  {data.periods.map(period => (
                    <td>{period.summary.usersAskingXthQuestion['5th+']}</td>
                  ))}
                </tr>
                <tr className="bg-info">
                  <th className="bg-info">User Rating Data</th>
                  {data.periods.map(() => (
                    <td />
                  ))}
                </tr>
                <tr>
                  <th>Num Questions Rated</th>
                  {data.periods.map(period => (
                    <td>{period.summary.numQuestionsRated}</td>
                  ))}
                </tr>
                <tr>
                  <th>Avg Rating</th>
                  {data.periods.map(period => (
                    <td>{period.summary.avgUserRating.toFixed(2)}</td>
                  ))}
                </tr>
                <tr className="bg-info">
                  <th className="bg-info">By Rating Reason</th>
                  {data.periods.map(() => (
                    <td />
                  ))}
                </tr>

                {data.meta.ratingReasons.map(reason => {
                  return (
                    <tr key={reason}>
                      <th>{reason}</th>
                      {data.periods.map(period => {
                        return <td>{period.byRatingReason[reason]}</td>;
                      })}
                    </tr>
                  );
                })}

                <tr className="bg-info">
                  <th className="bg-info">Auto Matching</th>
                  {data.periods.map(() => (
                    <td />
                  ))}
                </tr>

                <tr>
                  <th>Num Auto Populated</th>
                  {data.periods.map(period => (
                    <td>{period.questionMatchesQualitySummary.numAutoPopulated}</td>
                  ))}
                </tr>

                <tr>
                  <th>Num Good Match</th>
                  {data.periods.map(period => (
                    <td>{period.questionMatchesQualitySummary.numGoodMatch}</td>
                  ))}
                </tr> 

                <tr>
                  <th>Num Bad Match</th>
                  {data.periods.map(period => (
                    <td>{period.questionMatchesQualitySummary.numBadMatch}</td>
                  ))}
                </tr>

                <tr>
                  <th>% Good Match</th>
                  {data.periods.map(period => (
                    <td>{Math.round((period.questionMatchesQualitySummary.numGoodMatch / (period.questionMatchesQualitySummary.numGoodMatch + period.questionMatchesQualitySummary.numBadMatch)) * 100)}%</td>
                  ))}
                </tr>

                <tr className="bg-info">
                  <th className="bg-info">Auto Publishing</th>
                  {data.periods.map(() => (
                    <td />
                  ))}
                </tr>

                <tr>
                  <th>Num Decide Auto Published</th>
                  {data.periods.map(period => (
                    <td>{period.questionMatchesQualitySummary.numDecideAutoPublished}</td>
                  ))}
                </tr>

                <tr>
                  <th>Num Actually Auto Published</th>
                  {data.periods.map(period => (
                    <td>{period.questionMatchesQualitySummary.numActuallyAutoPublished}</td>
                  ))}
                </tr>

                <tr>
                  <th>Num Decide Auto Published Perfect Publish</th>
                  {data.periods.map(period => (
                    <td>{period.questionMatchesQualitySummary.numDecideAutoPublishedPerfectPublish}</td>
                  ))}
                </tr>

                <tr>
                  <th>Num Decide Auto Published False Positive</th>
                  {data.periods.map(period => (
                    <td>{period.questionMatchesQualitySummary.numDecideAutoPublishedFalsePositive}</td>
                  ))}
                </tr>

                <tr className="bg-info">
                  <th className="bg-info">Auto Publishing - Not Published</th>
                  {data.periods.map(() => (
                    <td />
                  ))}
                </tr>

                <tr>
                  <th>Num Decide Do Not Publish</th>
                  {data.periods.map(period => (
                    <td>{period.questionMatchesQualitySummary.numDecideDoNotPublish}</td>
                  ))}
                </tr>

                <tr>
                  <th>Num Decide Do Not Publish Missed Opportunity</th>
                  {data.periods.map(period => (
                    <td>{period.questionMatchesQualitySummary.numDecideDoNotPublishMissedOpportunity}</td>
                  ))}
                </tr>

                <tr>
                  <th>Num Decide Do Not Publish Safely Caught</th>
                  {data.periods.map(period => (
                    <td>{period.questionMatchesQualitySummary.numDecideDoNotPublishSafelyCaught}</td>
                  ))}
                </tr>

                <tr>
                  <th>Num Skipped Already Published</th>
                  {data.periods.map(period => (
                    <td>{period.questionMatchesQualitySummary.numSkippedAlreadyPublished}</td>
                  ))}
                </tr>

                <tr>
                  <th>Num Skipped Edited Since Decision</th>
                  {data.periods.map(period => (
                    <td>{period.questionMatchesQualitySummary.numSkippedEditedSinceDecision}</td>
                  ))}
                </tr>

                <tr>
                  <th>Num Skipped Not Finalised Stage</th>
                  {data.periods.map(period => (
                    <td>{period.questionMatchesQualitySummary.numSkippedNotFinalisedStage}</td>
                  ))}
                </tr>

                <tr>
                  <th>Num Skipped Sensitive Content</th>
                  {data.periods.map(period => (
                    <td>{period.questionMatchesQualitySummary.numSkippedSensitiveContent}</td>
                  ))}
                </tr>
                
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
